import React from 'react';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import { ComponentType } from '../../types/enums';
import type { Component, Step, StepResult } from '../../types/types';

// Internal component imports
import Cappex from '../Step/variants/Cappex';
import ConfigurableStep from '../Step/variants/ConfigurableStep';

/**
 * Props passed to internal components
 */
export interface ComponentProps {
  currentStep?: Step;
  handlePrevious?: () => void;
  [key: string]: unknown;
}

/**
 * Internal component registry
 * Maps ComponentType to internal components
 */
const COMPONENT_REGISTRY: Record<string, React.FC<ComponentProps>> = {
  [ComponentType.CAPPEX]: Cappex,
  // Add more internal components as they are built:
  // [ComponentType.SALARY_CALCULATOR]: SalaryCalculator,
  // [ComponentType.CAREER_PATHWAY]: CareerPathway,
  // [ComponentType.BANNER_CTA]: BannerCTA,
};

export interface ComponentRendererProps {
  /**
   * The type of component to render
   */
  componentType: ComponentType | string;
  /**
   * Current step data
   */
  currentStep?: Step;
  /**
   * Additional props to pass to the component
   */
  componentProps?: Record<string, unknown>;
}

/**
 * Renders internal components by type
 * Uses an internal registry - no external injection
 */
export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  componentType,
  currentStep,
  componentProps,
}) => {
  const { handlePrevious } = useDynamicExperienceContext();

  // DSL-backed COMPONENT/RESULT steps short-circuit the registry lookup and
  // render through the `ConfigurableStep` bridge (DB-1.5 / child SAD ADR-15),
  // which installs the data-binding provider stack around the underlying
  // `<DslConfig>` Puck render. The `dslConfig` payload is resolved
  // server-side from the published config referenced by `dslConfigId`.
  // Re-mounting on `dslConfigId ?? id` guarantees a fresh DSM cache when
  // the user transitions between configurable steps (plan §3.8).
  const dslBackedStep = currentStep as
    | (Step & Partial<Component & StepResult>)
    | undefined;
  if (dslBackedStep?.dslConfigId) {
    if (componentType) {
      console.warn(
        `[ComponentRenderer] Step "${dslBackedStep.id ?? 'unknown'}" has both componentType "${componentType}" and dslConfigId "${dslBackedStep.dslConfigId}"; routing through DSL. Payloads should declare exactly one.`,
      );
    }
    return (
      <ConfigurableStep
        key={dslBackedStep.dslConfigId ?? dslBackedStep.id ?? 'configurable'}
        dslConfig={dslBackedStep.dslConfig}
        flowType={dslBackedStep.flowType}
        trafficSource={dslBackedStep.trafficSource}
      />
    );
  }

  const Component = COMPONENT_REGISTRY[componentType];

  if (!Component) {
    console.warn(
      `Component type "${componentType}" is not registered. ` +
        `Add it to COMPONENT_REGISTRY in ComponentRenderer.tsx`,
    );
    return null;
  }

  return (
    <Component
      currentStep={currentStep}
      handlePrevious={handlePrevious}
      {...componentProps}
    />
  );
};

/**
 * Check if a component type is registered
 */
export function hasComponent(componentType: ComponentType | string): boolean {
  return componentType in COMPONENT_REGISTRY;
}

/**
 * Helper function to map step type to component type
 */
export function getComponentTypeFromStep(
  stepType: string,
  step?: Step,
): ComponentType | string | null {
  switch (stepType) {
    case 'BANNER_CTA':
      return ComponentType.BANNER_CTA;
    case 'COMPONENT':
      return (step as any)?.componentType || null;
    default:
      return null;
  }
}

export default ComponentRenderer;
